import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  automationTemplateService, 
  AutomationTemplate 
} from '../../services/automationTemplateService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import AutomationTemplateAnalytics from './AutomationTemplateAnalytics';

type TabType = 'templates' | 'analytics';

const AdminAutomationTemplateManager: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useAdminTheme();
  const { showSuccess, showError } = useNotification();
  
  const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('templates');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'cart_abandonment', label: 'Cart Abandonment' },
    { value: 'win_back', label: 'Win-Back' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 're_engagement', label: 'Re-engagement' },
    { value: 'upsell', label: 'Upsell' },
    { value: 'cross_sell', label: 'Cross-sell' },
    { value: 'product_recommendation', label: 'Product Recommendation' },
    { value: 'abandoned_browse', label: 'Abandoned Browse' },
    { value: 'post_purchase', label: 'Post-Purchase' },
    { value: 'review_request', label: 'Review Request' },
    { value: 'back_in_stock', label: 'Back in Stock' },
    { value: 'price_drop', label: 'Price Drop' },
    { value: 'vip', label: 'VIP' },
    { value: 'referral', label: 'Referral' },
    { value: 'educational', label: 'Educational' },
    { value: 'seasonal', label: 'Seasonal' },
    { value: 'other', label: 'Other' }
  ];

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const allTemplates = await automationTemplateService.getAllTemplates();
      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      showError('Failed to load automation templates');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // showError is stable from context, safe to omit

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only load once on mount

  const handleCreateNew = () => {
    sessionStorage.removeItem('admin_template_builder_automation_id');
    navigate('/admin/automation-template-editor?mode=template&return=/admin/control-panel-7x9k2m/automation-templates');
  };

  const handleEdit = (template: AutomationTemplate) => {
    navigate(`/admin/automation-template-editor?mode=template&templateId=${template.id}&return=/admin/control-panel-7x9k2m/automation-templates`);
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      await automationTemplateService.deleteTemplate(templateId);
      showSuccess('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showError('Failed to delete template');
    }
  };

  const handleToggleActive = async (template: AutomationTemplate) => {
    try {
      await automationTemplateService.toggleTemplateActive(template.id!, !template.isActive);
      showSuccess(`Template ${!template.isActive ? 'activated' : 'deactivated'} successfully`);
      loadTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
      showError('Failed to update template status');
    }
  };

  const handleDuplicate = async (template: AutomationTemplate) => {
    try {
      await automationTemplateService.duplicateTemplate(template.id!);
      showSuccess('Template duplicated successfully');
      loadTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      showError('Failed to duplicate template');
    }
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Statistics
  const stats = {
    total: templates.length,
    active: templates.filter(t => t.isActive).length,
    inactive: templates.filter(t => !t.isActive).length,
    public: templates.filter(t => t.isPublic).length,
    private: templates.filter(t => !t.isPublic).length,
    premium: templates.filter(t => t.isPremium).length,
    totalUsage: templates.reduce((sum, t) => sum + (t.usageCount || 0), 0),
    totalDownloads: templates.reduce((sum, t) => sum + (t.downloadCount || 0), 0)
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading templates...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Automation Template Manager
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Create and manage pre-built automation workflow templates
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4 mb-8">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Templates</div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.total}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active</div>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Inactive</div>
            <div className="text-2xl font-bold text-red-600">{stats.inactive}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Public</div>
            <div className="text-2xl font-bold text-blue-600">{stats.public}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Private</div>
            <div className="text-2xl font-bold text-orange-600">{stats.private}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Premium</div>
            <div className="text-2xl font-bold text-purple-600">{stats.premium}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Usage</div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.totalUsage}</div>
          </div>
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Downloads</div>
            <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.totalDownloads}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => { setActiveTab('templates'); }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              📋 Templates ({templates.length})
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              📊 Analytics
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'templates' && (
          <>
            {/* Filters and Actions */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`px-4 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-purple-500`}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <button
                onClick={handleCreateNew}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium whitespace-nowrap"
              >
                + Create Template
              </button>
            </div>

            {/* Templates Grid */}
            {filteredTemplates.length === 0 ? (
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-12 text-center`}>
                <div className="text-6xl mb-4">🔄</div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  No templates found
                </h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
                  {searchQuery || selectedCategory !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Get started by creating your first automation template'
                  }
                </p>
                {!searchQuery && selectedCategory === 'all' && (
                  <button
                    onClick={handleCreateNew}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    Create First Template
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTemplates.map((template) => (
                  <div 
                    key={template.id} 
                    className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow hover:shadow-lg transition-shadow border ${
                      isDark ? 'border-gray-700' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {template.name}
                          </h3>
                          <div className="flex flex-wrap gap-2 mb-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              template.isActive 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {template.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {template.isRecommended && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                                ⭐ Recommended
                              </span>
                            )}
                            {template.isPremium && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                👑 Premium
                              </span>
                            )}
                            {!template.isPublic && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                🔒 Private
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {template.description}
                      </p>

                      {/* Info */}
                      <div className={`text-xs space-y-1 mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <div>📂 Category: <span className="font-medium">{template.type}</span></div>
                        <div>📧 Channel: <span className="font-medium">{template.channel}</span></div>
                        <div>⏱️ Duration: <span className="font-medium">{template.estimatedDuration}</span></div>
                        <div>🔢 Steps: <span className="font-medium">{template.steps.length}</span></div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded p-2 text-center`}>
                          <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {template.usageCount || 0}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Usage</div>
                        </div>
                        <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded p-2 text-center`}>
                          <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {template.downloadCount || 0}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Downloads</div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleEdit(template)}
                          className="flex-1 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(template)}
                          className={`flex-1 px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                            template.isActive
                              ? 'text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                              : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                          }`}
                        >
                          {template.isActive ? '⏸️ Deactivate' : '▶️ Activate'}
                        </button>
                        <button
                          onClick={() => handleDuplicate(template)}
                          className="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                          title="Duplicate"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => handleDelete(template.id!)}
                          className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'analytics' && (
          <AutomationTemplateAnalytics templates={templates} />
        )}
      </div>
    </div>
  );
};

export default AdminAutomationTemplateManager;

